import React, { useEffect, useMemo, useState } from 'react';
import SettingContainer from '../SettingContainer';
import { useConfig } from '../../../DataStore/ConfigContext';
import Notification from '../../Common/Notification';
import { useTheme } from '../../../DataStore/ThemeContext';
import functions from '../../../util/functions';
import { Skeleton, SkeletonCircle } from '../../../util/skeleton/Skeleton';

/* ---------- helpers ---------- */
const initialsOf = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('') || 'U';

const StatusBadge = ({ status }) => {
  const isActive = String(status).toLowerCase() === 'active';
  return (
    <span className={`badge ${isActive ? 'badge-success' : 'badge-warning'}`} style={{ letterSpacing: '.02em' }}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
};

const RolePill = ({ role }) => (
  <span className="badge badge-light border text-uppercase" style={{ letterSpacing: '.04em' }}>
    <i className="fa fa-user-shield mr-1" />
    {role?.role_name || '—'}
  </span>
);

const Avatar = ({ photo, name }) => (
  <div
    className="rounded-circle d-flex align-items-center justify-content-center mr-3"
    style={{
      width: 44,
      height: 44,
      background: '#EEF2F6',
      color: '#0F172A',
      fontWeight: 700,
      overflow: 'hidden',
      flex: '0 0 44px',
    }}
  >
    {photo ? (
      <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    ) : (
      initialsOf(name)
    )}
  </div>
);

/* ---------- Card item ---------- */
const UserCard = ({ user, selected, onSelect }) => {
  const fullName = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim();

  return (
    <div
      className={`card shadow-sm ${selected ? 'border-primary' : 'border-0'}`}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(user)}
      onKeyDown={(e) => (e.key === 'Enter' ? onSelect(user) : null)}
      style={{ transition: 'box-shadow .2s, transform .2s' }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
      onMouseLeave={(e) => (e.currentTarget.style.transform = 'none')}
    >
      <div className="card-body d-flex flex-column ml-2">
        <div className="d-flex align-items-center">
          <Avatar photo={user.photo} name={fullName} />
          <div className="flex-grow-1">
            <div className="font-weight-semibold d-flex align-items-center">
              <span className="mr-2">{fullName || '—'}</span>
              {selected && <i className="fa fa-check-circle text-primary" aria-label="selected" />}
            </div>
            <div className="text-muted small">{user.username}</div>
          </div>
        </div>

        <div className="mt-3 d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
          <RolePill role={user.role} />
          <StatusBadge status={user.status} />
        </div>

        <div className="mt-auto pt-3 text-muted small">
          <div className="d-flex justify-content-between">
            <span><i className="fa fa-phone mr-1" /> {user.phone_number || '—'}</span>
            <span><i className="fa fa-clock mr-1" /> {functions.formatDate(user.updated_at)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Toolbar (search + status/role filters) ---------- */
const Toolbar = ({ query, setQuery, status, setStatus, role, setRole, roles = [] }) => (
  <div className="d-flex flex-wrap align-items-center mb-3 m-3" style={{ gap: 12 }}>
    <div className="input-group" style={{ maxWidth: 340 }}>
      <div className="input-group-prepend">
        <span className="input-group-text bg-white"><i className="fa fa-search" /></span>
      </div>
      <input
        type="text"
        className="form-control"
        placeholder="Search name or username…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
    </div>

    <div className="input-group" style={{ maxWidth: 200 }}>
      <div className="input-group-prepend">
        <span className="input-group-text bg-white"><i className="fa fa-toggle-on" /></span>
      </div>
      <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
        <option value="">All status</option>
        <option value="Active">Active</option>
        <option value="Inactive">Inactive</option>
      </select>
    </div>

    <div className="input-group" style={{ maxWidth: 220 }}>
      <div className="input-group-prepend">
        <span className="input-group-text bg-white"><i className="fa fa-user-shield" /></span>
      </div>
      <select className="form-control" value={role} onChange={(e) => setRole(e.target.value)}>
        <option value="">All roles</option>
        {roles.map((r) => (
          <option value={r.role_name} key={r.role_name}>{r.role_name}</option>
        ))}
      </select>
    </div>
  </div>
);

/* ---------- Skeleton grid while loading ---------- */
const UsersSkeleton = () => (
  <div className="row">
    {Array.from({ length: 6 }).map((_, i) => (
      <div className="col-lg-4 col-md-6 mb-3" key={i}>
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <div className="d-flex align-items-center">
              <SkeletonCircle size={44} />
              <div className="ml-3" style={{ width: '70%' }}>
                <Skeleton w="90%" h={14} />
                <Skeleton w="60%" h={12} style={{ marginTop: 6 }} />
              </div>
            </div>
            <div className="mt-3">
              <Skeleton w="40%" h={22} />
              <Skeleton w="30%" h={22} style={{ marginLeft: 8 }} />
            </div>
            <div className="mt-3">
              <Skeleton w="100%" h={10} />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ---------- Main component ---------- */
const SystemUsers = () => {
  const { openSettings } = useTheme();
  const [activeRecord, setActiveRecord] = useState(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [roleFilter, setRole] = useState('');

  const { getUsers, userListing, dispatch, loadingUsers, errorUsers, userRoles, setModifiedUser } = useConfig();

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNew = () => openSettings('Invite New User', 'ConfigNewUser');
  const handleDelete = () => activeRecord && dispatch({ type: 'deletedUser', payload: activeRecord });
  const handleChangeRole = () => activeRecord && setModifiedUser({ show: true, user: activeRecord, callback: () => getUsers(true) });

  const rolesForFilter = useMemo(() => {
    // if provided by store; otherwise derive from listing
    if (Array.isArray(userRoles) && userRoles.length) return userRoles;
    const names = Array.from(new Set((userListing || []).map((u) => u.role?.role_name).filter(Boolean)));
    return names.map((n, i) => ({ id: i + '', role_name: n }));
  }, [userRoles, userListing]);

  const filtered = useMemo(() => {
    const list = Array.isArray(userListing) ? userListing : [];
    const q = query.trim().toLowerCase();
    return list
      .filter((u) => (q ? (u.full_name || '').toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q) : true))
      .filter((u) => (status ? String(u.status).toLowerCase() === status.toLowerCase() : true))
      .filter((u) => (roleFilter ? String(u.role?.role_name || '').toLowerCase() === roleFilter.toLowerCase() : true))
      .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
  }, [userListing, query, status, roleFilter]);

  const menu = () => (
    <>
      <button type="button" tabIndex="0" className="dropdown-item" onClick={handleNew}>
        <i className="dropdown-icon fa-regular fa-paper-plane"></i>
        <span>Invite User</span>
      </button>
       <button type="button" tabIndex="0" className="dropdown-item" disabled={!activeRecord} onClick={handleChangeRole}>
        <i className="dropdown-icon fa fa-user-shield text-success" /> <span>Change Role</span>
      </button>
      <button type="button" tabIndex="0" className="dropdown-item" onClick={() => getUsers(true)}>
        <i className="dropdown-icon fa fa-sync"></i>
        <span>Refresh</span>
      </button>
      <div className="dropdown-divider"></div>
      <button type="button" tabIndex="0" className="dropdown-item" disabled={!activeRecord} onClick={handleDelete}>
        <i className="dropdown-icon fa fa-trash-alt text-danger" /> <span>Remove User</span>
      </button>
    </>
  );

  return (
    <SettingContainer title="System Users" icon="fa fa-user" Menu={menu}>
      {/* Toolbar */}
      <Toolbar
        query={query}
        setQuery={setQuery}
        status={status}
        setStatus={setStatus}
        role={roleFilter}
        setRole={setRole}
        roles={rolesForFilter}
      />

      {/* States */}
      {errorUsers && <Notification type="danger" message={String(errorUsers)} />}

      {(!userListing && !errorUsers) || loadingUsers ? (
        <UsersSkeleton />
      ) : Array.isArray(userListing) && userListing.length === 0 ? (
        <div className="text-center p-5 border rounded">
          <div className="mb-2">
            <span className="rounded-circle d-inline-flex align-items-center justify-content-center bg-light" style={{ width: 64, height: 64 }}>
              <i className="fa fa-users text-muted" />
            </span>
          </div>
          <div className="h6">No users yet</div>
          <div className="text-muted mb-3">Invite teammates to collaborate on your account.</div>
          <button className="btn btn-primary btn-sm" onClick={handleNew}>
            <i className="fa fa-paper-plane mr-1" />
            Invite User
          </button>
        </div>
      ) : (
        <div className="row">
          {filtered.map((u) => (
            <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={u.user_id}>
              <UserCard user={u} selected={activeRecord?.user_id === u.user_id} onSelect={setActiveRecord} />
            </div>
          ))}
        </div>
      )}
    </SettingContainer>
  );
};

export default SystemUsers;
